/**
 * PanelCanvasV1.tsx
 *
 * Canvas component for the v1 dashboard viewing experience.
 * Implements the full panel-grid layout per mockup 16 (`2026-04-23-16-canvas-loaded-v3.html`).
 *
 * Responsibilities:
 *   1. Read panel definitions from dashboardPanels Redux slice (ordered by `panel.order`).
 *   2. Read per-panel display data from dashboardView.panelDataMap.
 *   3. Apply the KPI Strip adjacency rule (kpiStripGrouper) to produce render groups.
 *   4. Render:
 *        - DomainProgressBanner (while isSyncing)
 *        - KpiStripGroup  → full-width horizontal strip card (grid, 1px gap on border color)
 *        - KpiSoloGroup   → PanelRenderer (kpi, solo hero mode, text-5xl)
 *        - SinglePanelGroup → PanelRenderer (non-kpi)
 *   5. Show per-domain error banners (DomainErrorBanner, PR 6 scope — stub here).
 *
 * Layout grid:
 *   - KPI strip:       col-span-full, repeat(N, 1fr) where N = strip.panels.length
 *   - Panel-grid:      2-col grid; table panels are col-span-full
 *   - Empty state:     shown when no panels are present
 *
 * Accessible labels per brand guide §16:
 *   - canvas aria-label="Dashboard canvas"
 *   - KPI strip region aria-label="KPI summary strip"
 *
 * Brand guide §13 KPI Strip adjacency rule:
 *   - 2–6 consecutive kpi panels → one strip card with vertical dividers
 *   - Single kpi → solo hero (handled by PanelRenderer)
 *   - Non-kpi panels → standard panel card
 */

import React from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/redux'
import type { DashboardPanel } from '@/types/Dashboards.types'
import type { DashboardPanelV1, PanelType } from '@/types/DashboardV1.types'
import {
  selectIsSyncing,
  selectAllDomainStatuses,
  selectPanelDataMap,
  selectEditingPanelId,
  selectFailedDomainIds,
} from '@/redux/slice/dashboardViewSlice'
import { groupPanels } from './kpiStripGrouper'
import { selectResolveDomainDisplayName } from '@/redux/slice/dashboardDomainRegistrySlice'
import type { KpiStripGroup, KpiSoloGroup, SinglePanelGroup } from './kpiStripGrouper'
import { PanelRenderer } from './PanelRenderer'
import { DomainProgressBanner } from './DomainProgressBanner'
import type { DomainProgressEntry } from './DomainProgressBanner'
import { cn } from '@/lib/utils'

// ─── PanelCardWrapper ─────────────────────────────────────────────────────────

/**
 * Wraps a PanelRenderer with panel edit mode treatment (brand guide §36, §32).
 *
 * v2 change (brand guide §36 CEO ruling 2026-05-07):
 *  - Zero hover affordances: no border change, no ⋮ menu, no "Edit with AI" button.
 *  - Panels are purely static visual artifacts at hover.
 *  - Edit mode dimming (§32) is preserved: chat-driven panel edit mode (entered when
 *    the backend recognises an edit intent from the user's message) dims other panels
 *    and shows the "Editing" chip on the target panel.
 */
interface PanelCardWrapperProps {
  panelId: string
  panelTitle: string
  editingPanelId: string | null
  children: React.ReactNode
}

const PanelCardWrapper: React.FC<PanelCardWrapperProps> = ({
  panelId,
  panelTitle,
  editingPanelId,
  children,
}) => {
  const isEditingThis = editingPanelId === panelId
  const isEditingOther = editingPanelId !== null && editingPanelId !== panelId

  return (
    <div
      className={cn(
        // Base: relative (for the "Editing" chip), default border
        'relative border border-border rounded-xl',
        // No hover state changes — panels are read-only visual artifacts in v1
        // (brand guide §36 v2: zero hover affordances)
        // Edit mode — this panel is editing target: 2px accent border (brand guide §32)
        isEditingThis && 'border-2 border-accent shadow-[0_0_0_3px_hsl(var(--accent-background))]',
        // Edit mode — another panel is editing: dim + non-interactive (brand guide §32)
        isEditingOther && 'opacity-55 pointer-events-none',
      )}
      role="region"
      aria-label={isEditingOther ? `${panelTitle} panel — dimmed while editing another panel` : `${panelTitle} panel`}
      aria-hidden={isEditingOther ? true : undefined}
    >
      {/* "Editing" chip — shown on the active editing panel (brand guide §32) */}
      {isEditingThis && (
        <div
          className="absolute top-2.5 left-2.5 z-10 bg-accent text-[10px] text-white font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          aria-label="This panel is being edited"
        >
          Editing
        </div>
      )}

      {/* No hover controls of any kind (brand guide §36 v2) */}
      {children}
    </div>
  )
}

// ─── Panel definition adaptor ─────────────────────────────────────────────────

/**
 * Adapts the legacy DashboardPanel (missing pivot_table) or the new DashboardPanelV1
 * shape to a GroupablePanel by narrowing to the fields used by kpiStripGrouper.
 *
 * Note: DashboardPanel.panelType does not include 'pivot_table'. We cast via `as string`
 * to avoid a TS error when a v1 panel with pivot_table is stored in the legacy slice.
 * This is a carry-forward item — tracked in known-technical-debt.md.
 */
function toGroupable(panel: DashboardPanel | DashboardPanelV1): { id: string; panelType: string } {
  return { id: panel.id, panelType: panel.panelType as string }
}

// ─── KPI Strip Cell ───────────────────────────────────────────────────────────

/**
 * Individual cell inside a KPI strip card.
 * Renders per brand guide §13: text-[22px] value, 11px label, optional delta chip.
 */
const KpiStripCell: React.FC<{
  title: string
  value: string | number
  delta?: string | null
  deltaDirection?: 'up' | 'down' | 'neutral' | null
}> = ({ title, value, delta, deltaDirection }) => {
  const deltaColor =
    deltaDirection === 'up'
      ? 'text-[#15803D]'
      : deltaDirection === 'down'
      ? 'text-destructive'
      : 'text-muted-foreground'

  return (
    <div className="bg-card px-4 py-3.5">
      <p className="text-[11px] font-medium text-secondary uppercase tracking-[0.05em] mb-1">
        {title}
      </p>
      <p className="text-[22px] font-bold text-primary leading-none">
        {String(value ?? '--')}
      </p>
      {delta != null && (
        <p className={cn('text-[11.5px] font-medium mt-0.5', deltaColor)}>{delta}</p>
      )}
    </div>
  )
}

// ─── KPI Strip Group Renderer ─────────────────────────────────────────────────

const KpiStripRenderer: React.FC<{
  group: KpiStripGroup
  panelDataMap: ReturnType<typeof selectPanelDataMap>
  definitions: (DashboardPanel | DashboardPanelV1)[]
  /** PR 6: amber border when data is stale due to sync failure */
  isStale?: boolean
}> = ({ group, panelDataMap, definitions, isStale }) => {
  const count = group.panels.length
  const gridCols = `repeat(${count}, minmax(0, 1fr))`

  return (
    <div
      className={cn('w-full overflow-hidden rounded-[10px] border', isStale ? 'border-warning' : 'border-border')}
      style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '1px', background: 'hsl(var(--border))' }}
      role="region"
      aria-label={isStale ? 'KPI summary strip — data may be stale' : 'KPI summary strip'}
      data-testid="kpi-strip"
    >
      {group.panels.map(p => {
        const def = definitions.find(d => d.id === p.id)
        const panelData = panelDataMap[p.id]

        // Determine display values
        let value: string | number = '--'
        let delta: string | null = null
        let deltaDirection: 'up' | 'down' | 'neutral' | null = null

        if (panelData?.status === 'success' && panelData.displayData) {
          const d = panelData.displayData as { value?: string | number; delta?: string | null; deltaDirection?: 'up' | 'down' | 'neutral' | null }
          value = d.value ?? '--'
          delta = d.delta ?? null
          deltaDirection = d.deltaDirection ?? null
        }

        return (
          <KpiStripCell
            key={p.id}
            title={def?.title ?? p.id}
            value={value}
            delta={delta}
            deltaDirection={deltaDirection}
          />
        )
      })}
    </div>
  )
}

// ─── Panel grid layout helpers ────────────────────────────────────────────────

/**
 * Returns true for panels that should span the full grid width.
 * Currently: table panels get full width (matches mockup 16 roster table).
 */
function isFullWidthPanel(panelType: string): boolean {
  return panelType === 'table'
}

// ─── PanelCanvasV1 ────────────────────────────────────────────────────────────

interface PanelCanvasV1Props {
  /**
   * Panel definitions in canvas order. If omitted, the component reads from
   * dashboardPanels Redux slice via useSelector.
   * Accepting as a prop enables isolated unit testing.
   */
  panels?: (DashboardPanel | DashboardPanelV1)[]
  /**
   * PR 6: When true, panels show a "Data as of {staleAsOf}" amber footer note
   * and the KPI strip shows an amber border.
   * Set when dashboardError?.code === 'AUTH_EXPIRED' (CampMinder credential expired).
   */
  showStaleNote?: boolean
  /** PR 6: ISO date string (e.g. "Apr 22, 2026") displayed in the stale note. */
  staleAsOf?: string
  /**
   * Callback for the MULTI_FETCH_PARTIAL_FAILURE retry button (YoY Slice 2.3).
   *
   * Threaded through to each PanelRenderer as `onRetry`. When provided, the
   * PartialFailureCallout renders an actionable "Retry" button. When absent,
   * the callout still surfaces the informational message without a button.
   *
   * Bound by Canvas.tsx to `triggerSync(token, dashboardId)`.
   * Optional for backward compat — existing callers need no change.
   *
   * YoY TDD §3.4.
   */
  onPanelRetry?: () => void
  /**
   * Callback for the `[+ Add YYYY]` restore button on §38.7 comparison-only
   * disabled panels (YoY Slice 3.1).
   *
   * Threaded through to each PanelRenderer as `onRestoreComparisonSeason`.
   * Fires AI chat-confirmed mutation — see
   * `feedback_no_direct_mutation_buttons_post_dashboard_save` axiom.
   *
   * Bound by Canvas.tsx to `dispatch(sendEditChatMessageThunk(token, 'Compare to YYYY'))`.
   * Optional for backward compat — existing callers need no change.
   *
   * YoY TDD §3.5.
   */
  onRestoreComparisonSeason?: () => void
}

export const PanelCanvasV1: React.FC<PanelCanvasV1Props> = ({ panels: panelsProp, showStaleNote, staleAsOf, onPanelRetry, onRestoreComparisonSeason }) => {
  // Read panels from Redux if not provided via props.
  // We select the raw array and sort in render to avoid a new-reference selector warning.
  const panelsFromStore = useSelector(
    (state: RootState) => state.dashboardPanels.panels as unknown as DashboardPanel[]
  )
  const rawPanels = panelsProp ?? panelsFromStore
  // Sort is stable for same input since it copies — only runs when rawPanels changes.
  const panels = rawPanels.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  const isSyncing = useSelector(selectIsSyncing)
  const domainStatuses = useSelector(selectAllDomainStatuses)
  const panelDataMap = useSelector(selectPanelDataMap)
  const editingPanelId = useSelector(selectEditingPanelId)
  // Slice 3.2 — set of qualified domain ids whose backing credential was
  // unavailable on the most recent sync. Drives the "(cached)" suffix on
  // affected panels' date-meta footer (brand guide §37f).
  //
  // Per the §0.3 invariant, the BE preserved cached `dashboard_panel_data`
  // rows bit-identically for these domains; this set is purely a render-hint
  // for the visible marker, not a data-mutation signal.
  const failedDomainIds = useSelector(selectFailedDomainIds)
  // Registry-aware display-name resolver. Replaces the deleted
  // resolveDomainDisplayName helper from src/constants/domains.ts.
  const resolveDomainDisplayName = useSelector(selectResolveDomainDisplayName)

  // ── Domain progress banner entries ──────────────────────────────────────────
  const progressEntries: DomainProgressEntry[] = Object.entries(domainStatuses).map(
    ([domainId, entry]) => ({
      domainId,
      displayName: resolveDomainDisplayName(domainId),
      tagStatus:
        entry.status === 'success'
          ? 'done'
          : entry.status === 'fetching'
          ? 'active'
          : 'pending',
    })
  )

  // Sub-label: the currently active domain's message
  const activeDomain = Object.entries(domainStatuses).find(([, e]) => e.status === 'fetching')
  const sublabel = activeDomain
    ? `Fetching ${resolveDomainDisplayName(activeDomain[0])} data — this usually takes a few seconds.`
    : null

  // ── Group panels by KPI Strip adjacency rule ────────────────────────────────
  const groups = groupPanels(panels.map(toGroupable))

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (panels.length === 0 && !isSyncing) {
    return (
      <div
        className="flex-1 flex items-center justify-center text-muted-foreground text-sm"
        data-testid="panel-canvas-empty"
        aria-label="Dashboard canvas"
      >
        No panels yet. Chat with the AI to add panels to this dashboard.
      </div>
    )
  }

  // Canvas aria-label reflects edit mode (mockup 21)
  const canvasAriaLabel = editingPanelId
    ? `Dashboard canvas — ${panels.find(p => p.id === editingPanelId)?.title ?? 'a panel'} is being edited`
    : 'Dashboard canvas'

  return (
    <div className="flex flex-col flex-1 min-w-0" aria-label={canvasAriaLabel} data-testid="panel-canvas-v1">
      {/* Domain progress narration banner (visible while syncing) */}
      {isSyncing && (
        <DomainProgressBanner
          domains={progressEntries}
          sublabel={sublabel}
        />
      )}

      {/* Panel canvas scroll area */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        {/* Panel-grid: 2-col for non-kpi, full-width for table/kpi-strip */}
        <div className="grid grid-cols-2 gap-4" data-testid="panel-grid">
          {groups.map((group, idx) => {
            if (group.kind === 'kpi_strip') {
              const stripGroup = group as KpiStripGroup
              // KPI strip: dims as a unit when another panel is in edit mode (mockup 21)
              // KPI cells are not individually editable via AI in v1
              const isStripDimmed = editingPanelId !== null
              return (
                <div
                  key={`strip-${idx}`}
                  className={cn(
                    'col-span-full',
                    isStripDimmed && 'opacity-55 pointer-events-none',
                  )}
                  aria-hidden={isStripDimmed ? true : undefined}
                >
                  <KpiStripRenderer
                    group={stripGroup}
                    panelDataMap={panelDataMap}
                    definitions={panels}
                    isStale={showStaleNote}
                  />
                </div>
              )
            }

            if (group.kind === 'kpi_solo') {
              const soloGroup = group as KpiSoloGroup
              const def = panels.find(p => p.id === soloGroup.panel.id)
              const panelTitle = def?.title ?? soloGroup.panel.id
              const panelType = (def?.panelType ?? 'kpi') as PanelType
              // Slice 3.2 — derive `isCached` from the panel definition's
              // `domainId` (DashboardPanelV1 optional field) against the
              // failed-domains snapshot. Legacy `DashboardPanel` lacks the
              // field, in which case the panel can never be cached-flagged
              // (acceptable: only V1 dashboard-era panels participate in the
              // credential-aware availability flow).
              const soloDomainId = (def as DashboardPanelV1 | undefined)?.domainId
              const soloIsCached =
                soloDomainId != null && failedDomainIds.has(soloDomainId)
              return (
                <div key={soloGroup.panel.id} className="col-span-full">
                  <PanelCardWrapper
                    panelId={soloGroup.panel.id}
                    panelTitle={panelTitle}
                    editingPanelId={editingPanelId}
                  >
                    <PanelRenderer
                      panelType={panelType}
                      title={panelTitle}
                      panelData={panelDataMap[soloGroup.panel.id]}
                      showStaleNote={showStaleNote}
                      staleAsOf={staleAsOf}
                      isCached={soloIsCached}
                      onRetry={onPanelRetry}
                      onRestoreComparisonSeason={onRestoreComparisonSeason}
                    />
                  </PanelCardWrapper>
                </div>
              )
            }

            // Single non-kpi panel
            const singleGroup = group as SinglePanelGroup
            const def = panels.find(p => p.id === singleGroup.panel.id)
            const panelType = (def?.panelType ?? 'table') as PanelType
            const panelTitle = def?.title ?? singleGroup.panel.id
            const fullWidth = isFullWidthPanel(panelType)
            const singleDomainId = (def as DashboardPanelV1 | undefined)?.domainId
            const singleIsCached =
              singleDomainId != null && failedDomainIds.has(singleDomainId)

            return (
              <div
                key={singleGroup.panel.id}
                className={fullWidth ? 'col-span-full' : undefined}
              >
                <PanelCardWrapper
                  panelId={singleGroup.panel.id}
                  panelTitle={panelTitle}
                  editingPanelId={editingPanelId}
                >
                  <PanelRenderer
                    panelType={panelType}
                    title={panelTitle}
                    panelData={panelDataMap[singleGroup.panel.id]}
                    showStaleNote={showStaleNote}
                    staleAsOf={staleAsOf}
                    isCached={singleIsCached}
                    onRetry={onPanelRetry}
                    onRestoreComparisonSeason={onRestoreComparisonSeason}
                  />
                </PanelCardWrapper>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
